import swal from "sweetalert";
import axios from "axios";
import { sweetAlertTimedSuccess } from "@assets/js/alerts";
export const groupBy = (arr, keyGetter, sortByIndex = false, sortDirection = 'asc') => {
    const output = [];
    const getOutputIndex = (itemIndex) => output.findIndex(item => item.index === itemIndex);
    for (let item of arr) {
        const itemIndex = keyGetter(item);
        const outputIndex = getOutputIndex(itemIndex);
        if (outputIndex === -1) {
            output.push({
                index: itemIndex,
                data: [item]
            });
        }
        else {
            output[outputIndex].data.push(item);
        }
    }
    if (sortByIndex) {
        output.sort((a, b) => {
            if (a.index === b.index) {
                return 0;
            }
            return sortDirection === 'asc'
                ? (a.index > b.index ? 1 : -1)
                : (a.index < b.index ? 1 : -1);
        });
    }
    return output;
};
export const randomString = () => [...Array(30)].map(() => Math.random().toString(36)[2]).join('');
export const Ymd = (date) => {
    const offset = date.getTimezoneOffset();
    date = new Date(date.getTime() - (offset * 60 * 1000));
    return date.toISOString().split('T')[0];
};
export const swalAxiosErrorResponse = (error, title) => {
    // Try and get the error message from the response data, unless one has been passed in
    title = title ?? error.response?.data?.message;
    // If the error is returned as a string, at this point assume that's our error message otherwise just use the
    // axios default message for the given code
    if (!title && error.response?.data && typeof error.response?.data === 'string') {
        title = error.response.data;
    }
    let params = {
        title: title ?? error.message,
        icon: 'error'
    };
    if (error.response?.data?.errors) {
        const innerHtml = Object.values(error.response.data.errors)
            .map((errors) => errors.length > 1
            // Get the nested error messages - which I've never seen
            ? ('<ul><li>' + errors.join('</li><li>') + '</li></ul>')
            // Get the top-level error messages
            : errors.find(Boolean))
            .join('</li><li>');
        params.content = {
            element: 'ul',
            attributes: {
                innerHTML: '<li>' + innerHtml + '</li>'
            }
        };
    }
    return swal(params);
};
export const asset = (path) => (window._asset || '') + path;
export const mutateConfirmationUrl = (url) => {
    const confirmationUrl = new URL(url);
    confirmationUrl.searchParams.set('confirm', '1');
    return confirmationUrl.toString();
};
/**
 * @param url the initial URL to send the request to. After confirmation, will add confirm=1
 * @param afterDeletion a function to run after the deletion action has succeeded
 * @param title
 */
export const attemptAxiosDeletion = async (url, afterDeletion, title = 'Are you sure?') => {
    try {
        // Get the confirmation request
        const response = await axios.delete(url);
        // Show a dialog to confirm the deletion
        swal({
            title: response.data.title || title,
            text: response.data.message?.replace(/<br\s*\/?>/gi, '\n') ?? 'Delete this resource?',
            icon: 'warning',
            buttons: {
                cancel: true,
                confirm: {
                    text: response.data.confirmButtonText || 'Yes',
                    closeModal: false
                }
            },
            dangerMode: true
        })
            // Handle the confirmation
            .then((isConfirm) => {
            if (isConfirm) {
                return axios
                    // Perform the confirmed deletion
                    .delete(mutateConfirmationUrl(url))
                    .then(response_1 => {
                    // Show a success message
                    sweetAlertTimedSuccess({
                        text: response_1.data.message.replace(/<br\s*\/?>/gi, '\n'),
                    }).then();
                    if (afterDeletion) {
                        afterDeletion();
                    }
                });
            }
        });
    }
    catch (err) {
        return await swalAxiosErrorResponse(err);
    }
};
export const attemptAxiosConfirmation = async (url, afterConfirmation, method = 'PUT') => axios
    .request({ url: url, method: method })
    .then((initialResponse) => sweetAlert({
    icon: 'warning',
    title: 'Are you sure?',
    text: initialResponse.data.message,
    buttons: {
        cancel: true,
        confirm: true
    }
}))
    .then(confirm => {
    if (confirm) {
        return axios
            .request({
            url: mutateConfirmationUrl(url),
            method: method
        })
            .then(response => afterConfirmation(response));
    }
})
    .catch(swalAxiosErrorResponse);
// a fungible confirm helper - pass in title and message, get back a promise that resolves to true or false
export function confirmAction(title, text, params = null) {
    return sweetAlert({
        ...params,
        title,
        text,
        icon: 'warning',
        buttons: {
            cancel: true,
            confirm: {
                text: 'Yes'
            },
        },
        dangerMode: true
    });
}
export const axiosFileDownload = async (url, method = 'GET', request) => axios
    .request({ url: url, method: method, responseType: 'blob', data: request })
    .catch(async (err) => {
    // Handle errors, which we will assume are returned as JSON from the server
    const errorText = await err.response.data.text();
    const error = JSON.parse(errorText);
    // The error was a string, so just show it
    if (typeof error == 'string') {
        swalAxiosErrorResponse(err.response, error || 'Error attempting to download file').then();
    }
    else {
        // Add the error data to the response object
        err.response.data = { ...err.response.data, ...error };
        swalAxiosErrorResponse(err.response, error.message ?? 'Error attempting to download file').then();
    }
    throw err;
})
    .then(response => {
    const contentDispositionHeader = response.headers['content-disposition'];
    // Extract the filename from the content-disposition header
    const filenameMatch = contentDispositionHeader.match(/filename=["']?([^"';]+)["']?/);
    // Use the extracted filename or provide a default name
    const filename = filenameMatch ? filenameMatch[1] : 'downloaded-file';
    const blob = new Blob([response.data], { type: response.headers['content-type'] });
    const objectURL = URL.createObjectURL(blob);
    // Create a temporary anchor element
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = objectURL;
    a.download = filename;
    // Append the anchor to the document and trigger the download
    document.body.appendChild(a);
    a.click();
    // Clean up by removing the anchor and revoking the object URL
    document.body.removeChild(a);
    URL.revokeObjectURL(objectURL);
});
export function debounce(func, delay) {
    let timeoutId;
    return function () {
        const context = this;
        const args = Array.from(arguments);
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            func.apply(context, args);
        }, delay);
    };
}
/**
 * Memoize a function that returns a promise so that the promise is not called multiple times
 * @param func
 */
export function memoize(func) {
    const cache = {};
    // Allow the cache to be cleared so that the memoized function can be called again fresh
    const clearCache = () => {
        for (const key in cache) {
            delete cache[key];
        }
    };
    const memoizedFunc = async (arg) => {
        const argsString = JSON.stringify(arg);
        if (!cache[argsString]) {
            cache[argsString] = func(arg);
        }
        return cache[argsString];
    };
    return { memoizedFunc, clearCache };
}
